// src/components/MsoDefinitionEditor.tsx
//
// ★★ ANNEX A 22 — the draft editor's body: effective date, precedence, rounding, the inputs the
// engine requires, the applicability variants, and the decision table as a grid (conditions as
// chips-with-controls, shares as percentages).
//
// ══ WHAT IS STORED, AND WHAT IS SHOWN ══
//
// A share is stored as WHOLE BASIS POINTS (1500) and shown as a percentage (15.00%). The box takes
// the percentage as typed and converts it by string arithmetic (`percentToBasisPoints`); a figure
// with a third decimal place is refused rather than rounded, because a rounded rate is a different
// rate. The box keeps what the officer typed while they type: converting on every keystroke would
// rewrite "15." into "15.00%" under their cursor.
//
// ⚠ **A validator message is shown AT the part it names.** The route's validator reports
// `table[2].shares[0]: officerBasisPoints must be ...`; this screen files each such message under
// the row, variant or setting it begins with (`errorsFor`), and `MsoRuleEditorPage` lists any that
// name no part, so no message is dropped for having no home.
import { useEffect, useId, useState } from 'react'
import { IconButton } from './IconButton'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { DatePicker } from './DatePicker'
import { FieldError, fieldErrorProps } from './FieldError'
import { MsoConditionListEditor } from './MsoConditionListEditor'
import { errorsFor } from '../lib/mso-editor'
import { plainRuleProblem } from '../lib/plain-words'
import {
  basisPointsLabel,
  humaniseToken,
  MSO_COMPONENTS,
  MSO_COMPONENT_LABELS,
  MSO_CONDITION_FIELD_NAMES,
  MSO_FIELD_LABELS,
  MSO_ROUNDING_LABELS,
  MSO_ROUNDING_MODES,
  percentToBasisPoints,
} from '../lib/mso-vocabulary'
import type { MsoDecisionRow, MsoFieldBounds, MsoRuleDefinition } from '../lib/types'

function ServerErrors({ messages }: { messages: readonly string[] }) {
  if (messages.length === 0) return null
  return (
    <ul role="alert" className="text-xs text-[var(--sgds-danger-color-emphasis)] list-disc pl-5 m-0">
      {messages.map((m) => (
        <li key={m}>{plainRuleProblem(m)}</li>
      ))}
    </ul>
  )
}

/** A percentage box that keeps the officer's own text and reports whole basis points. */
function PercentInput({
  label,
  basisPoints,
  onChange,
  disabled,
}: {
  label: string
  basisPoints: number
  onChange: (bp: number) => void
  disabled: boolean
}) {
  const errorId = `${useId()}-error`
  const [text, setText] = useState(() => basisPointsLabel(basisPoints).replace('%', ''))
  const parsed = percentToBasisPoints(text)
  const problem = 'error' in parsed ? parsed.error : null

  // A saved definition arriving from outside replaces what is typed; the officer's own typing does not.
  useEffect(() => {
    const current = percentToBasisPoints(text)
    if (!('basisPoints' in current) || current.basisPoints !== basisPoints) setText(basisPointsLabel(basisPoints).replace('%', ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basisPoints])

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <input
          type="text"
          inputMode="decimal"
          aria-label={label}
          className="pt-native-date"
          style={{ width: '6rem' }}
          disabled={disabled}
          value={text}
          placeholder="15.00"
          onChange={(e) => {
            setText(e.target.value)
            const next = percentToBasisPoints(e.target.value)
            if ('basisPoints' in next) onChange(next.basisPoints)
          }}
          {...fieldErrorProps(errorId, problem)}
        />
        <span aria-hidden="true">%</span>
      </div>
      <FieldError id={errorId} problem={problem} />
    </div>
  )
}

function nextRowId(table: readonly MsoDecisionRow[]): string {
  const used = new Set(table.map((r) => r.id))
  for (let n = table.length + 1; ; n += 1) {
    const candidate = `ROW-${n}`
    if (!used.has(candidate)) return candidate
  }
}

export interface DefinitionEditorProps {
  readonly definition: MsoRuleDefinition
  readonly onChange: (next: MsoRuleDefinition) => void
  readonly effectiveFrom: string
  readonly onEffectiveFromChange: (iso: string) => void
  /** The route validator's messages from the last Save. */
  readonly errors: readonly string[]
  readonly disabled?: boolean
  /** The server's refusal of the effective date (a back-date over a priced bill), shown at the date field. */
  readonly dateProblem?: string | null
  /** The server's published length bounds for decision-row ids and labels. */
  readonly bounds?: MsoFieldBounds | undefined
}

export function MsoDefinitionEditor({
  definition,
  onChange,
  effectiveFrom,
  onEffectiveFromChange,
  errors,
  disabled = false,
  dateProblem = null,
  bounds,
}: DefinitionEditorProps) {
  const baseId = useId()
  const [precedenceText, setPrecedenceText] = useState(String(definition.precedence))
  const precedenceError = !/^\d{1,4}$/.test(precedenceText.trim()) ? 'Enter the precedence as a whole number.' : null
  const set = (patch: Partial<MsoRuleDefinition>) => onChange({ ...definition, ...patch })
  const setRow = (index: number, next: MsoDecisionRow) =>
    set({ table: definition.table.map((r, i) => (i === index ? next : r)) })
  const fieldsAlphabetical = [...MSO_CONDITION_FIELD_NAMES].sort((a, b) =>
    (MSO_FIELD_LABELS[a] ?? a) < (MSO_FIELD_LABELS[b] ?? b) ? -1 : 1,
  )

  return (
    <div className="flex flex-col gap-6">
      <fieldset className="grid gap-4 sm:grid-cols-3 border-0 p-0 m-0" disabled={disabled}>
        <legend className="text-lg font-semibold mb-2">Settings</legend>
        <div className="flex flex-col gap-1 text-sm">
          <span id={`${baseId}-from`} className="font-semibold">
            Effective from
          </span>
          <DatePicker
            value={effectiveFrom}
            onChange={onEffectiveFromChange}
            labelledBy={`${baseId}-from`}
            required
            subject="effective date"
            {...(dateProblem !== null && { invalid: true, describedBy: `${baseId}-from-error` })}
          />
          <FieldError id={`${baseId}-from-error`} problem={dateProblem} />
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={`${baseId}-precedence`} className="font-semibold">
            Precedence
          </label>
          <input
            id={`${baseId}-precedence`}
            type="text"
            inputMode="numeric"
            className="pt-native-date"
            value={precedenceText}
            placeholder="e.g. 30"
            onChange={(e) => {
              setPrecedenceText(e.target.value)
              if (/^\d{1,4}$/.test(e.target.value.trim())) set({ precedence: Number(e.target.value.trim()) })
            }}
            {...fieldErrorProps(`${baseId}-precedence-error`, precedenceError ?? errorsFor(errors, 'precedence')[0] ?? null)}
          />
          <FieldError id={`${baseId}-precedence-error`} problem={precedenceError ?? errorsFor(errors, 'precedence')[0] ?? null} />
          <span className="text-xs text-[var(--sgds-body-color-subtle)]">
            Where two rules claim a bill, the lower number is chosen first.
          </span>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={`${baseId}-rounding`} className="font-semibold">
            Rounding
          </label>
          <select
            id={`${baseId}-rounding`}
            className="pt-native-date"
            value={definition.rounding.mode}
            onChange={(e) => set({ rounding: { ...definition.rounding, mode: e.target.value } })}
            {...fieldErrorProps(`${baseId}-rounding-error`, errorsFor(errors, 'rounding')[0] ?? null)}
          >
            {MSO_ROUNDING_MODES.map((m) => (
              <option key={m} value={m}>
                {MSO_ROUNDING_LABELS[m]}
              </option>
            ))}
          </select>
          <FieldError id={`${baseId}-rounding-error`} problem={errorsFor(errors, 'rounding')[0] ?? null} />
          <span className="text-xs text-[var(--sgds-body-color-subtle)]">
            The officer share is rounded to the cent per component; Government takes the remainder.
          </span>
        </div>
      </fieldset>

      <fieldset className="flex flex-wrap gap-x-4 gap-y-1 border-0 p-0 m-0" disabled={disabled}>
        <legend className="text-lg font-semibold mb-2">Inputs the engine must have</legend>
        {fieldsAlphabetical.map((field) => (
          <label key={field} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={definition.requiredInputs.includes(field)}
              onChange={(e) =>
                set({
                  requiredInputs: e.target.checked
                    ? [...definition.requiredInputs, field]
                    : definition.requiredInputs.filter((f) => f !== field),
                })
              }
            />
            {MSO_FIELD_LABELS[field] ?? humaniseToken(field)}
          </label>
        ))}
        <ServerErrors messages={errorsFor(errors, 'requiredInputs')} />
      </fieldset>

      <section aria-label="Applicability" className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Applicability</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch] m-0">
          The rule claims a bill when any one variant holds; within a variant every condition must hold.
        </p>
        {definition.applicability.map((variant, index) => (
          <div key={index} className="pt-well p-3 flex flex-col gap-2" data-variant={index}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm">Variant {index + 1}</span>
              <SgdsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || definition.applicability.length <= 1}
                onClick={() => set({ applicability: definition.applicability.filter((_, i) => i !== index) })}
                aria-label={`Remove variant ${index + 1}`}
              >
                Remove variant<span className="sr-only"> {index + 1}</span>
              </SgdsButton>
            </div>
            <MsoConditionListEditor
              name={`Variant ${index + 1}`}
              conditions={variant}
              disabled={disabled}
              onChange={(next) => set({ applicability: definition.applicability.map((v, i) => (i === index ? next : v)) })}
            />
            <ServerErrors messages={errorsFor(errors, `applicability[${index}]`)} />
          </div>
        ))}
        <div>
          <SgdsButton
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => set({ applicability: [...definition.applicability, []] })}
          >
            Add a variant
          </SgdsButton>
        </div>
      </section>

      <section aria-label="Decision table" className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Decision table</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch] m-0">
          Exactly one row must match a bill. Two matching rows, or none, is refused at calculation rather than guessed.
        </p>
        <div className="pt-table-wrap">
          <table className="pt-table">
            <caption className="sr-only">Decision table, one row per scenario</caption>
            <thead>
              <tr>
                <th scope="col" data-unsortable="a decision table has no order, only a unique match, so its rows are edited as written rather than sorted">
                  Row
                </th>
                <th scope="col" data-unsortable="a decision table has no order, only a unique match, so its rows are edited as written rather than sorted">
                  Applies when
                </th>
                <th scope="col" data-unsortable="a decision table has no order, only a unique match, so its rows are edited as written rather than sorted">
                  Officer share
                </th>
                <th scope="col" className="pt-no-print" data-unsortable="a decision table has no order, only a unique match, so its rows are edited as written rather than sorted">
                  Remove
                </th>
              </tr>
            </thead>
            <tbody>
              {definition.table.map((row, index) => (
                <tr key={index} data-row={index}>
                  <td>
                    <div className="flex flex-col gap-2">
                      <input
                        aria-label={`Row ${index + 1}, id`}
                        className="pt-native-date pt-mono"
                        disabled={disabled}
                        value={row.id}
                        {...(bounds !== undefined && { maxLength: bounds.rowId })}
                        placeholder="e.g. R4-DEPENDANT"
                        onChange={(e) => setRow(index, { ...row, id: e.target.value })}
                      />
                      <input
                        aria-label={`Row ${index + 1}, label`}
                        className="pt-native-date"
                        disabled={disabled}
                        value={row.label}
                        {...(bounds !== undefined && { maxLength: bounds.rowLabel })}
                        placeholder="Say what this row does"
                        onChange={(e) => setRow(index, { ...row, label: e.target.value })}
                      />
                      {bounds !== undefined && (
                        <span className="text-xs text-[var(--sgds-body-color-subtle)]">
                          Row id: {bounds.rowId - row.id.length} characters left. Label: {bounds.rowLabel - row.label.length} characters left.
                        </span>
                      )}
                      <ServerErrors messages={errorsFor(errors, `table[${index}]`)} />
                    </div>
                  </td>
                  <td>
                    <MsoConditionListEditor
                      name={`Row ${index + 1}`}
                      conditions={row.when}
                      disabled={disabled}
                      onChange={(next) => setRow(index, { ...row, when: next })}
                    />
                  </td>
                  <td>
                    <div className="flex flex-col gap-2">
                      {row.shares.map((share, shareIndex) => (
                        <div key={shareIndex} className="flex flex-wrap items-start gap-2" data-share={shareIndex}>
                          <select
                            aria-label={`Row ${index + 1}, share ${shareIndex + 1}, component`}
                            className="pt-native-date"
                            style={{ width: 'auto' }}
                            disabled={disabled}
                            value={share.component}
                            onChange={(e) =>
                              setRow(index, {
                                ...row,
                                shares: row.shares.map((s, i) => (i === shareIndex ? { ...s, component: e.target.value } : s)),
                              })
                            }
                          >
                            <option value="*">{MSO_COMPONENT_LABELS['*']}</option>
                            {MSO_COMPONENTS.map((c) => (
                              <option key={c} value={c}>
                                {MSO_COMPONENT_LABELS[c]}
                              </option>
                            ))}
                          </select>
                          <PercentInput
                            label={`Row ${index + 1}, share ${shareIndex + 1}, officer percentage`}
                            basisPoints={share.officerBasisPoints}
                            disabled={disabled}
                            onChange={(bp) =>
                              setRow(index, {
                                ...row,
                                shares: row.shares.map((s, i) => (i === shareIndex ? { ...s, officerBasisPoints: bp } : s)),
                              })
                            }
                          />
                          <IconButton
                            glyph="trash"
                            tone="danger"
                            label={`Remove row ${index + 1} share ${shareIndex + 1}`}
                            disabled={disabled || row.shares.length <= 1}
                            onClick={() => setRow(index, { ...row, shares: row.shares.filter((_, i) => i !== shareIndex) })}
                          />
                        </div>
                      ))}
                      <div>
                        <SgdsButton
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={disabled}
                          onClick={() =>
                            setRow(index, { ...row, shares: [...row.shares, { component: 'BILL_AMOUNT', officerBasisPoints: 0 }] })
                          }
                          aria-label={`Add a share to row ${index + 1}`}
                        >
                          Add a share
                        </SgdsButton>
                      </div>
                    </div>
                  </td>
                  <td className="pt-no-print">
                    <IconButton
                      glyph="trash"
                      tone="danger"
                      label={`Remove row ${index + 1}`}
                      disabled={disabled || definition.table.length <= 1}
                      onClick={() => set({ table: definition.table.filter((_, i) => i !== index) })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <SgdsButton
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() =>
              set({
                table: [
                  ...definition.table,
                  { id: nextRowId(definition.table), label: '', when: [], shares: [{ component: '*', officerBasisPoints: 0 }] },
                ],
              })
            }
          >
            Add a row
          </SgdsButton>
        </div>
      </section>
    </div>
  )
}
